import type { ApiClient } from "@ncp/api-client";
import { SaveError, notifySaveFailed, saveFailureText } from "@ncp/ui";
import type { StepLetter } from "../../case/stage/StepLetters";
import type { Widget } from "./widgets";
import { toolLabel } from "./toolNames";

/** A text in both languages: agents narrate as they work and the officer may switch language at any moment. */
export interface Bi { en: string; ar: string }
export const bi = (en: string, ar?: string): Bi => ({ en, ar: ar ?? en });
export const pick = (text: Bi | string | null | undefined, locale: string): string =>
  text == null ? "" : typeof text === "string" ? text : locale === "ar" ? text.ar || text.en : text.en;

/** The mission's waves follow the concentration procedure: each phase is one wave of agents and ends at the officer's gate. */
export type PhaseKey = "screening" | "publication" | "review" | "assessment" | "decision";
export const PHASES: PhaseKey[] = ["screening", "publication", "review", "assessment", "decision"];
export type PhaseState = "pending" | "active" | "done";

/** Squads group agents by craft; each has its own colour on the canvas. */
export type SquadKey = "documents" | "legal" | "market" | "intel" | "drafting" | "quality" | "runtime" | "memory";

/** The systems the agents draw on, as the officer sees them light up: the Ministry's own, government registers, commercial feeds, the open web. */
export type SourceKey = "documents" | "rules" | "registry" | "fcsc" | "databank" | "economics" | "dealwatch" | "news" | "corpus" | "web" | "memory" | "workflow" | "runtime";
export interface SourceUse { calls: number; errors: number; lastAt?: number; last?: Bi; agents: string[] }

/** Which source a tool call reaches, by the tool's name. */
const SOURCE_OF: Array<[RegExp, SourceKey]> = [
  [/^documents\./, "documents"],
  [/^ec\.(qualification|thresholds|readiness|checks|simplified|statutory-clock|efficiencies|coordination|publication)/, "rules"],
  [/^(analysis\.(figures|method)|legal\.rules)/, "rules"],
  [/^registry\./, "registry"],
  [/^analysis\.share-verification/, "fcsc"],
  [/^(analysis\.market-history|databank\.)/, "databank"],
  [/^economics\./, "economics"],
  [/^dealwatch\./, "dealwatch"],
  [/^news\./, "news"],
  [/^corpus\./, "corpus"],
  [/^web\./, "web"],
  [/^memory\./, "memory"],
  [/^workflow\./, "workflow"],
  [/^ai\./, "runtime"],
];
export const sourceOf = (tool: string): SourceKey | null => SOURCE_OF.find(([re]) => re.test(tool))?.[1] ?? null;

/**
 * One memory in play: recalled from the Department's institutional memory (earlier files, decisions, commitments, lessons, the practice
 * reviewers set) or remembered by this review for every later file (stored through /api/ec/memory).
 */
export interface MemoryItem {
  id: string;
  mode: "recall" | "remember";
  kind: "case" | "transaction" | "commitment" | "lesson" | "practice" | "fact" | "decision";
  title: Bi;
  detail?: Bi;
  source?: Bi;
  at: number;
  agentId: string;
  /** The stored memory's id (a remembered one), so the officer can make the review forget it. */
  serverId?: string;
  forgotten?: boolean;
}

/** A question to the platform's case assistant on behalf of an agent (online research), answered into that agent's box. */
export interface AssistantRequest { prompt: string; sources?: string[]; onUpdate?: (u: AssistantUpdate) => void }
export interface AssistantUpdate { text: string; tools: string[]; citations: AssistantCitation[]; streaming: boolean; error?: string }
export interface AssistantCitation { title: string; url?: string | null; publisher?: string | null; sourceType?: string; excerpt?: string | null }

export type AgentStatus = "queued" | "thinking" | "tool" | "writing" | "waiting" | "done" | "flagged" | "failed";
export type Tone = "ok" | "info" | "warn" | "risk";

export interface Thought { id: string; text: Bi; at: number; ms: number }
export interface ToolCall { id: string; name: string; args?: string; status: "running" | "ok" | "error"; summary?: Bi; ms?: number; at: number }
export interface Finding { id: string; agentId: string; tone: Tone; text: Bi; source?: Bi; at: number }
/** A direct exchange with one agent: the officer's question and the answer (streamed by the platform's case assistant, in that agent's role). */
export interface AgentMessage { id: string; from: "officer" | "agent"; text: string; at: number; streaming?: boolean; tools?: string[]; error?: string }

export interface AgentState {
  id: string;
  phase: PhaseKey;
  squad: SquadKey;
  icon: string;
  name: Bi;
  role: Bi;
  /** Agents shown together in one squad card (the document readers) share a group key. */
  group?: string;
  status: AgentStatus;
  progress: number;
  startedAt?: number;
  endedAt?: number;
  thoughts: Thought[];
  tools: ToolCall[];
  findings: Finding[];
  messages: AgentMessage[];
  widget?: Widget;
  sources: number;
  summary?: Bi;
  waitingOn?: string;
  /** A platform agent run streamed from the AI runtime (agent.step events). */
  runId?: string;
}

/** `needsText`: the option is taken only with the officer's own words in the question's field (an override's reasons). */
export interface QuestionOption { id: string; label: Bi; hint?: Bi; tone?: "primary" | "neutral" | "danger"; needsText?: boolean }
/** A field for the officer's own words (reasons in Arabic or English); nothing is written in it for the officer. */
export interface QuestionInput { label: Bi; hint?: Bi; minLength: number }
export interface Question {
  id: string;
  agentId: string;
  kind: "choice" | "gate";
  phase: PhaseKey;
  at: number;
  text: Bi;
  detail?: Bi;
  evidence?: Array<{ label: Bi; value: Bi; tone?: Tone }>;
  options: QuestionOption[];
  input?: QuestionInput;
  /** The letters the step put to the officer sends, in full, as the server rendered them (release 1.6): the card shows them before any answer. */
  letters?: StepLetter[];
  answer?: string;
  /** What the officer wrote in the question's field, as typed. */
  answerText?: string;
  answeredAt?: number;
  /** What happened after the officer answered (the transitions a gate made, or why the file could not move). */
  outcome?: Bi;
  outcomeTone?: Tone;
}

export type LeadItem =
  | { id: string; kind: "say"; at: number; text: Bi; phase: PhaseKey }
  | { id: string; kind: "relay"; at: number; agentId: string; findingId: string; phase: PhaseKey }
  | { id: string; kind: "question"; at: number; questionId: string; phase: PhaseKey }
  | { id: string; kind: "user"; at: number; text: string; phase: PhaseKey }
  | { id: string; kind: "phase"; at: number; phase: PhaseKey; text: Bi }
  | { id: string; kind: "chat"; at: number; text: string; streaming: boolean; tools: string[]; phase: PhaseKey; error?: string }
  | { id: string; kind: "memory"; at: number; memoryId: string; phase: PhaseKey };

export type LeadStatus = "idle" | "reading" | "planning" | "dispatching" | "listening" | "asking" | "concluding" | "done";
export interface PlanStep { id: string; label: Bi; agentIds: string[]; phase: PhaseKey }

export interface LogLine { id: string; at: number; agentId: string | "lead"; kind: "think" | "tool" | "result" | "find" | "ask" | "done" | "lead" | "spawn" | "error"; text: Bi }
export interface Packet { id: string; agentId: string; tone: Tone; at: number }

export interface MissionStats { tools: number; sources: number; tokens: number; findings: number; pages: number; answered: number }

export interface MissionState {
  caseId: string;
  caseNumber: string;
  title: Bi;
  status: "ready" | "running" | "paused" | "done" | "stopped";
  speed: number;
  phase: PhaseKey;
  phases: Record<PhaseKey, PhaseState>;
  /** The phase the canvas shows; follows the active phase unless the officer picks an earlier wave on the rail. */
  viewPhase: PhaseKey | null;
  agents: Record<string, AgentState>;
  order: string[];
  lead: { status: LeadStatus; line: Bi; items: LeadItem[]; plan: PlanStep[] };
  questions: Record<string, Question>;
  log: LogLine[];
  packets: Packet[];
  stats: MissionStats;
  /** Every source an agent has reached in this review, with its calls and the last thing asked of it. */
  sources: Partial<Record<SourceKey, SourceUse>>;
  /** What the review recalled from the Department's memory and what it remembered for later files. */
  memory: MemoryItem[];
  startedAt?: number;
  pausedAt?: number;
  pausedTotal: number;
  endedAt?: number;
  focus: string | null;
}

// The officer never sees which model an agent runs on (the platform's AI, not a vendor's): run steps name it in their details.
const MODEL_ID = /\b(?:model\s+)?(?:anthropic|openai|google|meta-llama|mistralai|x-ai|deepseek|qwen|cohere)\/[\w.:-]+|\b(?:claude|gpt|gemini|llama|mistral|grok|o\d)-[\w.:-]*\d[\w.:-]*/gi;

/** A platform run's step text as the canvas shows it: no model ids, and a tool's JSON result read as what it holds. */
export function runtimeText(text: string): string {
  let t = text.replace(MODEL_ID, "AI model").replace(/\s{2,}/g, " ");
  const json = t.search(/[{[]\s*"/);
  if (json >= 0) {
    const sources = (t.match(/"marker"\s*:/g) ?? []).length;
    const rows = (t.match(/},\s*{/g) ?? []).length + 1;
    t = `${t.slice(0, json).replace(/[\s—:–-]+$/, "")}${sources ? ` — ${sources} sources` : rows > 1 ? ` — ${rows} records` : ""}`;
  }
  return t.trim();
}

/** Thrown inside agent programs when the officer leaves or restarts the mission; programs simply unwind. */
export class MissionCancelled extends Error {
  constructor() { super("mission cancelled"); }
}

const LOG_LIMIT = 260;
const PACKET_TTL = 1800;
let seq = 0;
const uid = (prefix: string) => `${prefix}-${(++seq).toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

/** Characters an agent "types" per second at 1×: slow enough to read along, fast enough to feel busy. */
const TYPING_CPS = 58;

/**
 * Values the agents share while they work (the case file the lead reads first, what each agent established, the officer's answers).
 * An agent that needs another's result waits for it with `need`.
 */
export class Blackboard {
  private readonly values = new Map<string, unknown>();
  private readonly waiters = new Map<string, Array<(v: unknown) => void>>();

  set(key: string, value: unknown): void {
    this.values.set(key, value);
    const list = this.waiters.get(key);
    if (list) { this.waiters.delete(key); list.forEach((resolve) => resolve(value)); }
  }

  get<T>(key: string): T | undefined { return this.values.get(key) as T | undefined; }
  has(key: string): boolean { return this.values.has(key); }

  need<T>(key: string): Promise<T> {
    if (this.values.has(key)) return Promise.resolve(this.values.get(key) as T);
    return new Promise<T>((resolve) => {
      const list = this.waiters.get(key) ?? [];
      list.push((v) => resolve(v as T));
      this.waiters.set(key, list);
    });
  }
}

export interface SpawnSpec {
  id: string;
  phase: PhaseKey;
  squad: SquadKey;
  icon: string;
  name: Bi;
  role: Bi;
  group?: string;
  run: (ctx: AgentCtx) => Promise<void>;
}

/** What an agent program can do: narrate, call real tools, draw its intermediate result, report findings and ask the officer. */
export interface AgentCtx {
  readonly id: string;
  readonly api: ApiClient;
  readonly board: Blackboard;
  readonly locale: () => string;
  think(en: string, ar: string): Promise<void>;
  status(status: AgentStatus): void;
  tool<T>(name: string, args: string | undefined, run: () => Promise<T>, summarize?: (result: T) => Bi | string | null): Promise<T>;
  widget(widget: Widget): void;
  find(tone: Tone, en: string, ar: string, options?: { relay?: boolean; source?: Bi }): void;
  progress(value: number): void;
  sources(count: number): void;
  tokens(count: number): void;
  pages(count: number): void;
  ask(question: Omit<Question, "id" | "agentId" | "phase" | "kind" | "at">): Promise<string>;
  wait(ms: number): Promise<void>;
  done(summaryEn: string, summaryAr: string, tone?: Tone): void;
  attachRun(runId: string): void;
  /** Shows a memory recalled from the Department's institutional memory, in this agent's box and the lead's column. */
  recall(item: Omit<MemoryItem, "id" | "mode" | "at" | "agentId">, options?: { relay?: boolean }): void;
  /** Remembers something for every later file (the Department's memory); resolves once stored. */
  remember(input: RememberInput): Promise<void>;
  /** Asks the platform's case assistant (online research), its answer streaming into this agent's box. */
  assistant(request: AssistantRequest): Promise<AssistantUpdate>;
  /** Writes (or updates) this agent's own message in its box: an answer it is receiving as it streams. */
  answer(messageId: string, update: AssistantUpdate): void;
}

/** What the review remembers for later files: a fact of this file, or a practice reviewers set (a preference, it holds until changed). */
export interface RememberInput { key: string; kind: "fact" | "preference" | "note"; title: Bi; text: Bi; market?: string | null; parties?: string[]; memoryKind?: MemoryItem["kind"] }

/**
 * One agentic review of a concentration file. Holds the observable state of every agent, the lead's conversation and the officer's
 * answers; agent programs run concurrently against the platform's own endpoints and pace themselves so a person can follow them.
 * React reads it through `useSyncExternalStore` (notifications are batched per animation frame).
 */
export class Mission {
  readonly board = new Blackboard();
  private state: MissionState;
  private readonly listeners = new Set<() => void>();
  private frame: number | null = null;
  private cancelled = false;
  private resume: { promise: Promise<void>; release: () => void } | null = null;
  private readonly answers = new Map<string, (optionId: string) => void>();
  private readonly running = new Map<string, Promise<void>>();

  constructor(readonly api: ApiClient, readonly locale: () => string, init: { caseId: string; caseNumber: string; title: Bi }) {
    this.state = {
      caseId: init.caseId, caseNumber: init.caseNumber, title: init.title,
      status: "ready", speed: 1, phase: "screening",
      phases: { screening: "pending", publication: "pending", review: "pending", assessment: "pending", decision: "pending" },
      viewPhase: null, agents: {}, order: [],
      lead: { status: "idle", line: bi("Ready", "جاهز"), items: [], plan: [] },
      questions: {}, log: [], packets: [],
      stats: { tools: 0, sources: 0, tokens: 0, findings: 0, pages: 0, answered: 0 },
      sources: {}, memory: [],
      pausedTotal: 0, focus: null,
    };
  }

  // ---------------------------------------------------------------- store

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getSnapshot = (): MissionState => this.state;

  private set(update: (s: MissionState) => MissionState): void {
    if (this.cancelled) return;
    this.state = update(this.state);
    if (this.frame != null) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null;
      this.listeners.forEach((l) => l());
    });
  }

  private patchAgent(id: string, update: (a: AgentState) => AgentState): void {
    this.set((s) => {
      const agent = s.agents[id];
      return agent ? { ...s, agents: { ...s.agents, [id]: update(agent) } } : s;
    });
  }

  private log(agentId: string, kind: LogLine["kind"], text: Bi): void {
    this.set((s) => ({ ...s, log: [...s.log.slice(-(LOG_LIMIT - 1)), { id: uid("l"), at: Date.now(), agentId, kind, text }] }));
  }

  private bump(stat: keyof MissionStats, by: number): void {
    this.set((s) => ({ ...s, stats: { ...s.stats, [stat]: s.stats[stat] + by } }));
  }

  // ---------------------------------------------------------------- time

  /** Waits `ms` of mission time: faster at 2× or 4×, frozen while paused, abandoned when the mission is cancelled. */
  async sleep(ms: number): Promise<void> {
    let remaining = ms;
    while (remaining > 0) {
      if (this.cancelled) throw new MissionCancelled();
      if (this.resume) await this.resume.promise;
      const step = Math.min(60, remaining / this.state.speed);
      await delay(step);
      remaining -= step * this.state.speed;
    }
    if (this.cancelled) throw new MissionCancelled();
  }

  pause(): void {
    if (this.resume || this.state.status !== "running") return;
    let release = () => {};
    const promise = new Promise<void>((r) => { release = r; });
    this.resume = { promise, release };
    this.set((s) => ({ ...s, status: "paused", pausedAt: Date.now() }));
  }

  play(): void {
    if (!this.resume) return;
    const r = this.resume;
    this.resume = null;
    this.set((s) => ({ ...s, status: "running", pausedTotal: s.pausedTotal + (s.pausedAt ? Date.now() - s.pausedAt : 0), pausedAt: undefined }));
    r.release();
  }

  setSpeed(speed: number): void { this.set((s) => ({ ...s, speed })); }
  setViewPhase(phase: PhaseKey | null): void { this.set((s) => ({ ...s, viewPhase: phase })); }
  setFocus(agentId: string | null): void { this.set((s) => ({ ...s, focus: agentId })); }

  dispose(): void {
    this.cancelled = true;
    this.resume?.release();
    if (this.frame != null) window.cancelAnimationFrame(this.frame);
    this.listeners.clear();
  }

  get isCancelled(): boolean { return this.cancelled; }

  // ---------------------------------------------------------------- mission flow

  start(): void {
    this.set((s) => ({ ...s, status: "running", startedAt: Date.now() }));
  }

  finish(status: "done" | "stopped" = "done"): void {
    this.set((s) => ({ ...s, status, endedAt: Date.now(), lead: { ...s.lead, status: "done" } }));
  }

  setPhase(phase: PhaseKey, state: PhaseState): void {
    this.set((s) => ({ ...s, phase: state === "active" ? phase : s.phase, phases: { ...s.phases, [phase]: state }, viewPhase: state === "active" ? null : s.viewPhase }));
  }

  // ---------------------------------------------------------------- the lead

  leadStatus(status: LeadStatus, line?: Bi): void {
    this.set((s) => ({ ...s, lead: { ...s.lead, status, line: line ?? s.lead.line } }));
  }

  /** The lead says something in its column; the text is typed out, and the call returns once it has been read out. */
  async say(en: string, ar: string, phase?: PhaseKey): Promise<void> {
    const text = bi(en, ar);
    this.set((s) => ({ ...s, lead: { ...s.lead, items: [...s.lead.items, { id: uid("s"), kind: "say", at: Date.now(), text, phase: phase ?? s.phase }] } }));
    this.log("lead", "lead", text);
    await this.sleep(Math.min(5200, 500 + (en.length / TYPING_CPS) * 1000));
  }

  announcePhase(phase: PhaseKey, text: Bi): void {
    this.set((s) => ({ ...s, lead: { ...s.lead, items: [...s.lead.items, { id: uid("p"), kind: "phase", at: Date.now(), phase, text }] } }));
  }

  setPlan(phase: PhaseKey, steps: Array<{ label: Bi; agentIds: string[] }>): void {
    this.set((s) => ({ ...s, lead: { ...s.lead, plan: [...s.lead.plan.filter((p) => p.phase !== phase), ...steps.map((st) => ({ ...st, id: uid("plan"), phase }))] } }));
  }

  userSaid(text: string): string {
    const id = uid("u");
    this.set((s) => ({ ...s, lead: { ...s.lead, items: [...s.lead.items, { id, kind: "user", at: Date.now(), text, phase: s.phase }] } }));
    return id;
  }

  /** A free-text exchange with the platform's case assistant, shown in the lead's column while it streams. */
  chat(update: { id?: string; text: string; streaming: boolean; tools?: string[]; error?: string }): string {
    const id = update.id ?? uid("c");
    this.set((s) => {
      const existing = s.lead.items.find((i) => i.id === id);
      const item: LeadItem = { id, kind: "chat", at: existing?.at ?? Date.now(), text: update.text, streaming: update.streaming, tools: update.tools ?? [], phase: existing?.phase ?? s.phase, error: update.error };
      return { ...s, lead: { ...s.lead, items: existing ? s.lead.items.map((i) => (i.id === id ? item : i)) : [...s.lead.items, item] } };
    });
    return id;
  }

  /** The officer asks one agent something directly; returns the id the answer will be filed under. */
  officerAsks(agentId: string, text: string): string {
    const id = uid("m");
    this.patchAgent(agentId, (a) => ({ ...a, messages: [...a.messages, { id, from: "officer", text, at: Date.now() }] }));
    this.log(agentId, "ask", bi(text));
    return id;
  }

  /** The answer to the officer's direct question, as it streams. */
  agentAnswers(agentId: string, update: { id: string; text: string; streaming: boolean; tools?: string[]; error?: string }): void {
    this.patchAgent(agentId, (a) => {
      const existing = a.messages.find((x) => x.id === update.id);
      const message: AgentMessage = { id: update.id, from: "agent", text: update.text, at: existing?.at ?? Date.now(), streaming: update.streaming, tools: update.tools, error: update.error };
      return { ...a, messages: existing ? a.messages.map((x) => (x.id === update.id ? message : x)) : [...a.messages, message] };
    });
  }

  /** A question or a gate put to the officer; resolves with the option the officer picks. */
  askOfficer(agentId: string, kind: Question["kind"], question: Omit<Question, "id" | "agentId" | "phase" | "kind" | "at">): { id: string; answer: Promise<string> } {
    const id = uid("q");
    const answer = new Promise<string>((resolve) => this.answers.set(id, resolve));
    this.set((s) => ({
      ...s,
      questions: { ...s.questions, [id]: { ...question, id, agentId, kind, phase: s.phase, at: Date.now() } },
      lead: { ...s.lead, status: "asking", items: [...s.lead.items, { id: uid("i"), kind: "question", at: Date.now(), questionId: id, phase: s.phase }] },
    }));
    this.log(agentId, "ask", question.text);
    return { id, answer };
  }

  /** The officer's answer; an option that needs the officer's own words is taken only with them (at least the question's minimum). */
  answer(questionId: string, optionId: string, text?: string): void {
    const resolve = this.answers.get(questionId);
    if (!resolve) return;
    const asked = this.state.questions[questionId];
    const option = asked?.options.find((o) => o.id === optionId);
    const typed = text?.trim() ?? "";
    if (option?.needsText && typed.length < (asked?.input?.minLength ?? 1)) return;
    this.answers.delete(questionId);
    this.set((s) => {
      const q = s.questions[questionId];
      if (!q) return s;
      const waiting = Object.values(s.agents).some((a) => a.waitingOn && a.waitingOn !== questionId);
      return {
        ...s,
        questions: { ...s.questions, [questionId]: { ...q, answer: optionId, answerText: option?.needsText ? typed : undefined, answeredAt: Date.now() } },
        stats: { ...s.stats, answered: s.stats.answered + 1 },
        lead: { ...s.lead, status: waiting ? "asking" : s.lead.status === "asking" ? "listening" : s.lead.status },
      };
    });
    resolve(optionId);
  }

  settle(questionId: string, outcome: Bi, tone: Tone = "ok"): void {
    this.set((s) => {
      const q = s.questions[questionId];
      return q ? { ...s, questions: { ...s.questions, [questionId]: { ...q, outcome, outcomeTone: tone } } } : s;
    });
  }

  // ---------------------------------------------------------------- agents

  /** Puts an agent on the canvas and starts its program; the returned promise settles when the agent has finished (or failed). */
  spawn(spec: SpawnSpec): Promise<void> {
    const agent: AgentState = {
      id: spec.id, phase: spec.phase, squad: spec.squad, icon: spec.icon, name: spec.name, role: spec.role, group: spec.group,
      status: "queued", progress: 0, thoughts: [], tools: [], findings: [], messages: [], sources: 0,
    };
    this.set((s) => ({ ...s, agents: { ...s.agents, [spec.id]: agent }, order: s.order.includes(spec.id) ? s.order : [...s.order, spec.id] }));
    this.log(spec.id, "spawn", bi(`${spec.name.en} joined`, `انضم ${spec.name.ar}`));
    const ctx = this.context(spec.id);
    const run = (async () => {
      try {
        await this.sleep(250 + Math.random() * 500);
        this.patchAgent(spec.id, (a) => ({ ...a, status: "thinking", startedAt: Date.now() }));
        await spec.run(ctx);
        this.patchAgent(spec.id, (a) => (a.status === "done" || a.status === "flagged" ? a : { ...a, status: "done", progress: 1, endedAt: a.endedAt ?? Date.now() }));
      } catch (e) {
        if (e instanceof MissionCancelled || this.cancelled) return;
        const message = e instanceof Error ? e.message : String(e);
        this.patchAgent(spec.id, (a) => ({ ...a, status: "failed", endedAt: Date.now(), summary: bi(`Stopped: ${message}`, `توقف: ${message}`) }));
        this.log(spec.id, "error", bi(message));
      }
    })();
    this.running.set(spec.id, run);
    return run;
  }

  private context(id: string): AgentCtx {
    const mission = this;
    return {
      id,
      api: this.api,
      board: this.board,
      locale: this.locale,
      async think(en, ar) {
        const text = bi(en, ar);
        const ms = Math.min(4200, 350 + (en.length / TYPING_CPS) * 1000);
        mission.patchAgent(id, (a) => ({ ...a, status: a.status === "waiting" ? a.status : "thinking", thoughts: [...a.thoughts, { id: uid("t"), text, at: Date.now(), ms: ms / mission.state.speed }] }));
        mission.log(id, "think", text);
        mission.bump("tokens", Math.round(en.length / 3.6));
        await mission.sleep(ms + 180);
      },
      status(status) { mission.patchAgent(id, (a) => ({ ...a, status })); },
      async tool(name, args, run, summarize) {
        const call: ToolCall = { id: uid("tool"), name, args, status: "running", at: Date.now() };
        mission.patchAgent(id, (a) => ({ ...a, status: "tool", tools: [...a.tools, call] }));
        const label = toolLabel(name);
        mission.log(id, "tool", bi(label.en, label.ar));
        mission.bump("tools", 1);
        mission.touchSource(name, id, args ? bi(args) : undefined);
        const started = performance.now();
        try {
          const [result] = await Promise.all([run(), mission.sleep(650 + Math.random() * 700)]);
          const ms = Math.round(performance.now() - started);
          const raw = summarize ? summarize(result) : null;
          const summary = raw == null ? undefined : typeof raw === "string" ? bi(raw) : raw;
          mission.patchAgent(id, (a) => ({ ...a, status: "thinking", tools: a.tools.map((t) => (t.id === call.id ? { ...t, status: "ok", ms, summary } : t)) }));
          if (summary) mission.log(id, "result", bi(`${label.en} → ${summary.en}`, `${label.ar} ← ${summary.ar}`));
          return result;
        } catch (e) {
          if (e instanceof MissionCancelled) throw e;
          const ms = Math.round(performance.now() - started);
          const message = e instanceof Error ? e.message : String(e);
          mission.touchSource(name, id, undefined, true);
          mission.patchAgent(id, (a) => ({ ...a, tools: a.tools.map((t) => (t.id === call.id ? { ...t, status: "error", ms, summary: bi(message) } : t)) }));
          mission.log(id, "error", bi(`${label.en}: ${message}`, `${label.ar}: ${message}`));
          throw e;
        }
      },
      widget(widget) { mission.patchAgent(id, (a) => ({ ...a, widget })); },
      find(tone, en, ar, options) {
        const finding: Finding = { id: uid("f"), agentId: id, tone, text: bi(en, ar), source: options?.source, at: Date.now() };
        mission.patchAgent(id, (a) => ({ ...a, findings: [...a.findings, finding] }));
        mission.bump("findings", 1);
        mission.log(id, "find", finding.text);
        const relay = options?.relay ?? (tone === "warn" || tone === "risk");
        if (relay) {
          mission.set((s) => ({
            ...s,
            packets: [...s.packets.filter((p) => Date.now() - p.at < PACKET_TTL), { id: uid("pk"), agentId: id, tone, at: Date.now() }],
            lead: { ...s.lead, items: [...s.lead.items, { id: uid("r"), kind: "relay", at: Date.now(), agentId: id, findingId: finding.id, phase: s.phase }] },
          }));
        }
      },
      progress(value) { mission.patchAgent(id, (a) => ({ ...a, progress: Math.max(a.progress, Math.min(1, value)) })); },
      sources(count) {
        mission.patchAgent(id, (a) => ({ ...a, sources: a.sources + count }));
        mission.bump("sources", count);
      },
      tokens(count) { mission.bump("tokens", count); },
      pages(count) { mission.bump("pages", count); },
      async ask(question) {
        const { id: qid, answer } = mission.askOfficer(id, "choice", question);
        mission.patchAgent(id, (a) => ({ ...a, status: "waiting", waitingOn: qid }));
        mission.set((s) => ({ ...s, packets: [...s.packets, { id: uid("pk"), agentId: id, tone: "warn", at: Date.now() }] }));
        const choice = await answer;
        mission.patchAgent(id, (a) => ({ ...a, status: "thinking", waitingOn: undefined }));
        return choice;
      },
      wait: (ms) => mission.sleep(ms),
      done(summaryEn, summaryAr, tone) {
        const flagged = tone === "warn" || tone === "risk";
        mission.patchAgent(id, (a) => ({ ...a, status: flagged ? "flagged" : "done", progress: 1, endedAt: Date.now(), summary: bi(summaryEn, summaryAr) }));
        mission.log(id, "done", bi(summaryEn, summaryAr));
        mission.set((s) => ({ ...s, packets: [...s.packets.filter((p) => Date.now() - p.at < PACKET_TTL), { id: uid("pk"), agentId: id, tone: tone ?? "ok", at: Date.now() }] }));
      },
      attachRun(runId) { mission.patchAgent(id, (a) => ({ ...a, runId })); },
      recall(item, options) { mission.recallMemory(id, item, options?.relay ?? false); },
      remember: (input) => mission.rememberMemory(id, input),
      assistant: (request) => mission.askAssistant(request),
      answer(messageId, u) { mission.agentAnswers(id, { id: messageId, text: u.text, streaming: u.streaming, tools: u.tools, error: u.error }); },
    };
  }

  // ---------------------------------------------------------------- sources and memory

  /** Counts a call to the source a tool reaches (none for a tool outside the catalogue). */
  touchSource(tool: string, agentId: string, last?: Bi, error = false): void {
    const key = sourceOf(tool);
    if (!key) return;
    this.set((s) => {
      const use = s.sources[key] ?? { calls: 0, errors: 0, agents: [] };
      return { ...s, sources: { ...s.sources, [key]: { calls: use.calls + 1, errors: use.errors + (error ? 1 : 0), lastAt: Date.now(), last: last ?? use.last, agents: use.agents.includes(agentId) ? use.agents : [...use.agents, agentId] } } };
    });
  }

  recallMemory(agentId: string, item: Omit<MemoryItem, "id" | "mode" | "at" | "agentId">, relay: boolean): void {
    const memory: MemoryItem = { ...item, id: uid("mem"), mode: "recall", at: Date.now(), agentId };
    this.set((s) => ({
      ...s,
      memory: [...s.memory, memory],
      lead: relay ? { ...s.lead, items: [...s.lead.items, { id: uid("mi"), kind: "memory", at: Date.now(), memoryId: memory.id, phase: s.phase }] } : s.lead,
      packets: relay ? [...s.packets.filter((p) => Date.now() - p.at < PACKET_TTL), { id: uid("pk"), agentId, tone: "info", at: Date.now() }] : s.packets,
    }));
    this.log(agentId, "find", bi(`Recalled: ${item.title.en}`, `استُدعي: ${item.title.ar}`));
  }

  /** Stores a memory for later files and shows it as "Memory updated"; the stored row's id lets the officer undo it. */
  async rememberMemory(agentId: string, input: RememberInput): Promise<void> {
    const memory: MemoryItem = { id: uid("mem"), mode: "remember", kind: input.memoryKind ?? (input.kind === "preference" ? "practice" : "fact"), title: input.title, detail: input.text, at: Date.now(), agentId };
    this.set((s) => ({
      ...s,
      memory: [...s.memory, memory],
      lead: { ...s.lead, items: [...s.lead.items, { id: uid("mi"), kind: "memory", at: Date.now(), memoryId: memory.id, phase: s.phase }] },
    }));
    this.touchSource("memory.remember", agentId, input.title);
    const s = this.state;
    try {
      const saved = await this.api.post<{ id: string }>("/api/ec/memory", {
        key: input.key, kind: input.kind, text: input.text.en, textAr: input.text.ar, caseId: s.caseId, caseNumber: s.caseNumber,
        market: input.market ?? null, parties: input.parties ?? [], agent: this.state.agents[agentId]?.name.en ?? (agentId === "lead" ? "Case Lead" : agentId),
      });
      if (!saved?.id) throw new SaveError("unconfirmed", null);
      this.set((st) => ({ ...st, memory: st.memory.map((m) => (m.id === memory.id ? { ...m, serverId: saved.id } : m)) }));
      // "Remembered" only once the Department's memory stored it (S 2.8).
      this.log(agentId, "done", bi(`Remembered: ${input.title.en}`, `حُفظ في الذاكرة: ${input.title.ar}`));
    } catch (e) {
      const why = bi(`Not stored: ${saveFailureText(e, "en")}`, `لم يُحفظ: ${saveFailureText(e, "ar")}`);
      this.set((st) => ({ ...st, memory: st.memory.map((m) => (m.id === memory.id ? { ...m, source: why } : m)) }));
      this.log(agentId, "error", why);
    }
  }

  /** The officer takes a remembered item back: the Department's memory forgets it. */
  async forgetMemory(memoryId: string): Promise<void> {
    const m = this.state.memory.find((x) => x.id === memoryId);
    if (!m?.serverId || m.forgotten) return;
    try {
      await this.api.delete(`/api/ec/memory/${m.serverId}`);
    } catch (e) {
      // The item stays remembered, and the officer is told why it could not be taken back (S 2.8).
      notifySaveFailed(e, pick(bi("The memory was not taken back", "لم يُسترجع البند من الذاكرة"), this.locale()));
      return;
    }
    this.set((s) => ({ ...s, memory: s.memory.map((x) => (x.id === memoryId ? { ...x, forgotten: true } : x)) }));
  }

  private assistantBridge: ((request: AssistantRequest) => Promise<AssistantUpdate>) | null = null;
  /** The view hands the engine its way to the case assistant (one conversation for the whole review, one question at a time). */
  setAssistant(bridge: ((request: AssistantRequest) => Promise<AssistantUpdate>) | null): void { this.assistantBridge = bridge; }
  askAssistant(request: AssistantRequest): Promise<AssistantUpdate> {
    if (!this.assistantBridge) return Promise.reject(new Error("The case assistant is not connected."));
    return this.assistantBridge(request);
  }

  /**
   * Adds a step streamed from a platform agent run (agent.step) to the card that follows it: tool calls and retrievals as tool lines, the
   * rest as thoughts; the run's own Completed or Failed step finishes the card.
   */
  runtimeStep(agentId: string, step: { kind: string; title: string; detail?: string | null }): void {
    const title = runtimeText(step.title);
    const detail = step.detail ? runtimeText(step.detail) : "";
    step = { ...step, title, detail };
    const text = bi(detail ? `${title} — ${detail.slice(0, 220)}` : title);
    const finished = step.kind === "Completed" || step.kind === "Failed";
    const status: AgentStatus = step.kind === "Completed" ? "done" : step.kind === "Failed" ? "failed" : step.kind === "ToolCall" || step.kind === "Retrieval" ? "tool" : step.kind === "Draft" ? "writing" : "thinking";
    if (step.kind === "ToolCall" || step.kind === "Retrieval") {
      this.patchAgent(agentId, (a) => ({ ...a, status, progress: Math.min(0.95, a.progress + 0.06), tools: [...a.tools, { id: uid("tool"), name: step.title.replace(/^(Tool|Result): /, ""), status: "ok", at: Date.now(), summary: step.detail ? bi(step.detail.slice(0, 160)) : undefined }] }));
      this.bump("tools", 1);
    } else {
      this.patchAgent(agentId, (a) => ({
        ...a, status, progress: finished ? 1 : Math.min(0.95, a.progress + 0.05), endedAt: finished ? Date.now() : undefined,
        summary: finished ? bi(step.title) : a.summary,
        thoughts: [...a.thoughts, { id: uid("t"), text, at: Date.now(), ms: 600 }],
      }));
    }
    if (finished) this.board.set(`runDone:${agentId}`, step.kind);
    this.log(agentId, step.kind === "ToolCall" ? "tool" : finished ? "done" : "think", text);
  }

  /** Waits until every listed agent has finished. */
  async settled(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => this.running.get(id) ?? Promise.resolve()));
    if (this.cancelled) throw new MissionCancelled();
  }

  agent(id: string): AgentState | undefined { return this.state.agents[id]; }
}
